import type { Point2D } from '@/shared/utils/ocrImagePreprocess';

export const createDefaultPoints = (width: number, height: number): Point2D[] => [
  { x: width * 0.1, y: height * 0.1 },
  { x: width * 0.9, y: height * 0.1 },
  { x: width * 0.9, y: height * 0.9 },
  { x: width * 0.1, y: height * 0.9 },
];

export const normalizePoints = (
  candidatePoints: Point2D[] | null | undefined,
  width: number,
  height: number,
): Point2D[] | null => {
  if (!Array.isArray(candidatePoints) || candidatePoints.length !== 4) {
    return null;
  }

  const normalized = candidatePoints
    .map((point) => ({
      x: Number(point?.x),
      y: Number(point?.y),
    }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

  if (normalized.length !== 4) {
    return null;
  }

  return normalized.map((point) => ({
    x: Math.max(0, Math.min(width - 1, point.x)),
    y: Math.max(0, Math.min(height - 1, point.y)),
  }));
};

export const normalizeCornerPoints = (points: Point2D[] | null | undefined) =>
  normalizePoints(points, Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER)
    ?.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y)) ?? [];

export const getInitialOcrCornerPoints = (
  response: { corners?: Point2D[] } | null | undefined,
): Point2D[] => {
  const corners = Array.isArray(response?.corners) ? response.corners : [];
  if (corners.length !== 4) return [];
  return normalizeCornerPoints(corners).length === 4
    ? normalizeCornerPoints(corners)
    : [];
};

export const getBoundingBoxFromPoints = (points: Point2D[]) => {
  if (!Array.isArray(points) || points.length !== 4) return null;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    x: minX,
    y: minY,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
  };
};

const rotatePoints90 = (
  candidatePoints: Point2D[],
  width: number,
  height: number,
  clockwise = true,
): Point2D[] | null => {
  const normalized = normalizePoints(candidatePoints, width, height);
  if (!normalized) return null;

  if (clockwise) {
    return [normalized[3], normalized[0], normalized[1], normalized[2]].map(
      (point) => ({
        x: Math.max(0, Math.min(height - 1, height - 1 - point.y)),
        y: Math.max(0, Math.min(width - 1, point.x)),
      }),
    );
  }

  return [normalized[1], normalized[2], normalized[3], normalized[0]].map(
    (point) => ({
      x: Math.max(0, Math.min(height - 1, point.y)),
      y: Math.max(0, Math.min(width - 1, width - 1 - point.x)),
    }),
  );
};

export const rotatePointsByQuarterTurns = (
  candidatePoints: Point2D[],
  width: number,
  height: number,
  quarterTurns = 0,
): Point2D[] | null => {
  const normalizedQuarterTurns = ((quarterTurns % 4) + 4) % 4;
  let nextPoints = normalizePoints(candidatePoints, width, height);
  if (!nextPoints) return null;

  let nextWidth = width;
  let nextHeight = height;
  for (let i = 0; i < normalizedQuarterTurns; i += 1) {
    nextPoints = rotatePoints90(nextPoints, nextWidth, nextHeight, true)!;
    [nextWidth, nextHeight] = [nextHeight, nextWidth];
  }

  return nextPoints;
};

export const reverseRotatePointsByQuarterTurns = (
  candidatePoints: Point2D[],
  width: number,
  height: number,
  quarterTurns = 0,
): Point2D[] | null => {
  const normalizedQuarterTurns = ((quarterTurns % 4) + 4) % 4;
  let nextPoints = normalizePoints(candidatePoints, width, height);
  if (!nextPoints) return null;

  let nextWidth = width;
  let nextHeight = height;
  for (let i = 0; i < normalizedQuarterTurns; i += 1) {
    nextPoints = rotatePoints90(nextPoints, nextWidth, nextHeight, false)!;
    [nextWidth, nextHeight] = [nextHeight, nextWidth];
  }

  return nextPoints;
};

export const getBaseName = (name = 'image') => {
  const idx = name.lastIndexOf('.');
  return idx > 0 ? name.slice(0, idx) : name;
};
